import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { TradePair } from '@ui/types/tradePair';
import { useMemo } from 'react';

import { useTradePairBaseQtyInfo } from './useTradePairBaseQtyInfo';

export const useOpenInterest = (tradingPair: TradePair) => {
  const marketPrice = usePriceLatest(tradingPair.baseSymbol);

  const sumQtyInfos = useTradePairBaseQtyInfo(tradingPair);

  return useMemo(() => {
    // for longRatio and shortRatio, use aggregated values
    const totalQtySum = sumQtyInfos?.totalQtySum;
    const longQtySum = sumQtyInfos?.longQtySum;
    const shortQtySum = sumQtyInfos?.shortQtySum;

    const longRatioInSum =
      totalQtySum && longQtySum && !totalQtySum.isZero()
        ? longQtySum.div(totalQtySum)
        : undefined;
    const shortRatioInSum =
      totalQtySum && shortQtySum && !totalQtySum.isZero()
        ? shortQtySum.div(totalQtySum)
        : undefined;

    // for longQty and shortQty and their USD value, use single trading pair values
    const longOIUsd =
      marketPrice && tradingPair.longQty
        ? tradingPair.longQty.multipliedBy(marketPrice)
        : undefined;
    const shortOIUsd =
      marketPrice && tradingPair.shortQty
        ? tradingPair.shortQty.multipliedBy(marketPrice)
        : undefined;
    const totalQty = tradingPair.longQty.plus(tradingPair.shortQty);

    const longRatio =
      tradingPair.longQty && totalQty && !totalQty.isZero()
        ? tradingPair.longQty.div(totalQty)
        : undefined;
    const shortRatio =
      tradingPair.shortQty && totalQty && !totalQty.isZero()
        ? tradingPair.shortQty.div(totalQty)
        : undefined;

    return {
      longQty: tradingPair.longQty,
      longOIUsd,
      shortQty: tradingPair.shortQty,
      shortOIUsd,
      netOI: shortOIUsd ? longOIUsd?.minus(shortOIUsd) : undefined,
      longRatio,
      shortRatio,
      longRatioInSum,
      shortRatioInSum,
    };
  }, [
    marketPrice,
    sumQtyInfos?.longQtySum,
    sumQtyInfos?.shortQtySum,
    sumQtyInfos?.totalQtySum,
    tradingPair.longQty,
    tradingPair.shortQty,
  ]);
};
